import { CloseOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, Flex, Form, message, Typography } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import { colors } from "shared/theme/color";
import {
  modalButtonsTheme,
  outlinedButtonTheme,
} from "components/modals/ModalChrome";
import dayjs, { ManipulateType, Dayjs } from "dayjs";
import { useClashWarning } from "components/modals/ClashWarningModal";
import { showConfirmModal } from "components/modals/confirmModal";
import {
  useCreateScheduleBatchV2,
  useCreateScheduleV2,
} from "hooks/mutations/schedule-v2";
import {
  useCreateScheduleDraft,
  useDeleteScheduleDraft,
  useUpdateScheduleDraft,
} from "hooks/mutations/schedule-draft";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { getAllCustomerByBusinessId } from "hooks/queries/customer";
import { getCustomerPackageByGroup } from "hooks/queries/customerpackage";
import { getAllStaffByBusinessId } from "hooks/queries/staff";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { translate } from "shared/languages";
import PATHS from "shared/constants/paths";
import { ClassType, CustomerPackage } from "shared/types";
import { AutoCompleteScope, ClassCategory, UserType } from "shared/types/enum";
import { getActualRemainingCredits, packageActivation, packageValidity } from "shared/utils";
import {
  combineDateTime,
  DEFAULT_DATE_SUBMIT_FORMAT,
  DEFAULT_TIMEZONE,
  now,
  tzDate,
} from "shared/utils/dayjs";
import { buildRRule, RecurrenceEnd, RecurrenceType } from "shared/utils/rrule";
import {
  ScheduleGhostPreview,
  useScheduleState,
} from "../filter/ScheduleFilterContext";
import { ClassSection } from "./ClassSection";
import { ClientPackageSection } from "./ClientPackageSection";
import {
  DAYJS_TO_RRULE_DAY,
  exceedsPackageBounds,
  isUntilDateTooClose,
} from "./RecurrenceSelect";
import {
  applyCreateScheduleResumeDraft,
  buildCreateScheduleResumeDraft,
  buildScheduleDraftPayload,
  CreateScheduleResumeDraft,
} from "shared/utils/scheduleResumeDraft";
import { SaveDraftClosePrompt } from "./SaveDraftClosePrompt";
import { TimeSection } from "./TimeSection";

// Width of the fixed create rail — index.tsx reserves the matching in-flow
// space so the calendar clears it.
export const CREATE_PANEL_WIDTH = 720;

interface PerDaySeries {
  days: string[];
  time: Dayjs;
  startDate: Dayjs;
  // Only set in count mode: this series' share of the TOTAL occurrence count.
  count?: number;
}

// Groups the selected weekdays by their chosen start time — days sharing a
// time stay in one series (BYDAY=MO,WE @09:00), a different time gets its own
// series — and derives each series' first occurrence date (the first date
// on/after the start date whose weekday is in the group), so every series'
// DTSTART weekday is in its own BYDAY, as the server requires. Count mode
// distributes N TOTAL occurrences chronologically across the union of selected
// days (identical semantics to a single BYDAY=...;COUNT=N rrule); a series
// whose share is zero never occurs and is dropped.
const buildPerDaySeries = (
  scheduleDate: Dayjs,
  days: string[],
  timeByDay: Map<string, Dayjs>,
  totalCount?: number
): PerDaySeries[] => {
  const start = tzDate(scheduleDate).startOf("day");

  const countByDay = new Map<string, number>();
  if (totalCount !== undefined) {
    const daySet = new Set(days);
    let cursor = start;
    let collected = 0;
    while (collected < totalCount) {
      const code = DAYJS_TO_RRULE_DAY[cursor.day()];
      if (daySet.has(code)) {
        countByDay.set(code, (countByDay.get(code) ?? 0) + 1);
        collected += 1;
      }
      cursor = cursor.add(1, "day");
    }
  }

  const groups = new Map<string, string[]>();
  for (const day of days) {
    const key = timeByDay.get(day)!.format("HH:mm");
    const group = groups.get(key);
    if (group) group.push(day);
    else groups.set(key, [day]);
  }

  const series: PerDaySeries[] = [];
  groups.forEach((groupDays) => {
    const count =
      totalCount !== undefined
        ? groupDays.reduce((sum, day) => sum + (countByDay.get(day) ?? 0), 0)
        : undefined;
    if (count === 0) return;

    const groupSet = new Set(groupDays);
    let firstDate = start;
    while (!groupSet.has(DAYJS_TO_RRULE_DAY[firstDate.day()])) {
      firstDate = firstDate.add(1, "day");
    }
    series.push({
      days: groupDays,
      time: timeByDay.get(groupDays[0])!,
      startDate: firstDate,
      count,
    });
  });
  return series;
};

// The date of `dayCode`'s occurrence within [weekStart, weekStart+6] (a week
// has exactly one of each weekday).
const dateInWeek = (weekStart: Dayjs, dayCode: string): Dayjs => {
  let cursor = tzDate(weekStart).startOf("day");
  for (let i = 0; i < 7; i++) {
    if (DAYJS_TO_RRULE_DAY[cursor.day()] === dayCode) return cursor;
    cursor = cursor.add(1, "day");
  }
  return cursor; // unreachable for a real 7-day week
};

interface RecurrenceEndCondition {
  type: "until" | "count";
  until?: Dayjs;
  count?: number;
}

// Every selected day whose occurrence falls within [weekStart, weekEnd] — the
// currently VISIBLE Jadwal week, not necessarily the first — honoring the
// recurrence's actual start date and end condition, so ghost boxes follow
// the admin navigating to a later week while creating, but a week the
// recurrence has already ended before (or hasn't started yet) shows none.
// Count-mode occurrences are counted chronologically across ALL selected
// days combined (mirrors buildPerDaySeries/the real RRULE COUNT semantics
// above), so a week far beyond a small COUNT correctly shows nothing.
const occurrencesInWeek = (
  scheduleDate: Dayjs,
  days: string[],
  endCondition: RecurrenceEndCondition,
  weekStart: Dayjs,
  weekEnd: Dayjs
): string[] => {
  const start = tzDate(scheduleDate).startOf("day");

  if (endCondition.type === "until") {
    if (weekEnd.isBefore(start)) return [];
    return days.filter((day) => {
      const date = dateInWeek(weekStart, day);
      if (date.isBefore(start)) return false;
      if (
        endCondition.until &&
        date.isAfter(tzDate(endCondition.until).endOf("day"))
      ) {
        return false;
      }
      return true;
    });
  }

  // Count mode: walk day-by-day from the start, counting only selected
  // days, stopping once the count is exhausted or we've passed the visible
  // week — whichever comes first.
  const totalCount = endCondition.count ?? 0;
  if (totalCount <= 0) return [];
  const daySet = new Set(days);
  const found = new Set<string>();
  let cursor = start;
  let collected = 0;
  while (collected < totalCount && !cursor.isAfter(weekEnd)) {
    const code = DAYJS_TO_RRULE_DAY[cursor.day()];
    if (daySet.has(code)) {
      collected += 1;
      if (!cursor.isBefore(weekStart)) {
        found.add(code);
      }
    }
    cursor = cursor.add(1, "day");
  }
  return Array.from(found);
};

interface CreateScheduleDrawerProps {
  // Set when the panel is reopened after a round trip to a client's detail
  // page (see ClientPackageSection.tsx's "Kelola Paket Klien" button and
  // schedule/index.tsx's resume effect) — restores class/time/client
  // selections instead of starting the form over.
  resumeDraft?: CreateScheduleResumeDraft | null;
  onResumeDraftConsumed?: () => void;
}

const CreateScheduleDrawer = ({
  resumeDraft,
  onResumeDraftConsumed,
}: CreateScheduleDrawerProps = {}) => {
  const {
    state,
    setCreateDrawerOpen,
    createDraft,
    setCreateDraft,
    setGhostPreviews,
  } = useScheduleState();
  const navigate = useNavigate();
  const [form] = useForm();
  const [selectedClass, setSelectedClass] = useState<ClassType>();
  const [customerIds, setCustomerIds] = useState<string>("");
  // Whether the admin has actually edited anything since the panel opened (or
  // since a draft/seed was applied) — gates the close prompt and the Simpan
  // Draft button. useState, not a ref: the button's disabled state must
  // re-render. Form onValuesChange only fires on user-driven control changes
  // (never setFieldsValue), so seeds and resumed drafts start clean;
  // handleClassChange also sets it directly because its resetFields clears
  // AntD's own touched state, which is why isFieldsTouched can't be the gate.
  const [isDirty, setIsDirty] = useState(false);
  // The schedule_draft row this panel was resumed from, when it was. Re-saving
  // PUTs that row instead of creating a duplicate; a successful Jadwalkan
  // deletes it; Buang leaves it untouched.
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  // Reported by TimeSection: timing currently conflicts with the selected
  // instructor's working hours/leave (either the shared field or, in
  // per-day mode, at least one day) — kept out of canSubmit's own state so
  // TimeSection stays the single source of truth for availability checks.
  const [timingBlockedByAvailability, setTimingBlockedByAvailability] =
    useState(false);
  const { mutate: createScheduleV2, isPending } = useCreateScheduleV2();
  const { mutate: createScheduleBatchV2, isPending: isBatchPending } =
    useCreateScheduleBatchV2();
  const { mutate: createDraftMutate, isPending: isCreatingDraft } =
    useCreateScheduleDraft();
  const { mutate: updateDraftMutate, isPending: isUpdatingDraft } =
    useUpdateScheduleDraft();
  const { mutate: deleteDraftSilently } = useDeleteScheduleDraft(true);
  const isSavingDraft = isCreatingDraft || isUpdatingDraft;
  const { checkAndConfirm, checkManyAndConfirm, clashWarning } =
    useClashWarning();

  const scheduleDate = useWatch("schedule_date", form) as Dayjs | undefined;
  const instructorId = useWatch("instructor_id", form);
  // Resolved plain-string instructor id — instructor_id uses labelInValue,
  // so the watched value is {value, label}, not a plain string. Same
  // resolution pattern already used ad hoc at the ghost-preview effect below
  // and in onSubmit; computed once here so it can also be passed down to
  // TimeSection for the availability picker.
  const resolvedInstructorId: string | undefined =
    instructorId?.value ?? instructorId;
  const startTime = useWatch("start_time", form) as Dayjs | undefined;
  const formValues = useWatch([], form);

  const selectedCustomerIds = useMemo(
    () => (customerIds ? customerIds.split(",").filter(Boolean) : []),
    [customerIds]
  );

  // ── Queries ──
  const { data: classes, isLoading: classLoading } =
    getAllClassTypeByBusinessId();
  const { data: businessSettings } = useBusinessSettings();
  // Employee management hard filter: only instructors assigned to the
  // selected class type are selectable. Omitted entirely (not just
  // client-hidden) when the master or the class-assignment sub-flag is off,
  // so studios that haven't opted into that specific sub-feature see every
  // instructor exactly as before.
  const { data: instructors, isLoading: instructorsLoading } =
    getAllStaffByBusinessId(UserType.Instructor, {
      is_active: true,
      ...(businessSettings?.employee_management_enabled &&
      businessSettings?.employee_management_class_assignment_enabled &&
      selectedClass
        ? { class_type_id: selectedClass.id }
        : {}),
    });
  const { data: customers, isLoading: customerLoading } =
    getAllCustomerByBusinessId({ is_active: true });
  const { data: customerPackages, isLoading: customerPackageLoading } =
    getCustomerPackageByGroup({
      customer_ids: customerIds,
      class_id: selectedClass?.id ?? "",
      // Packages are offered for the occurrence being created, not for
      // today: a backdated class should be paid for by the package that
      // covered that date (even if it has since expired), never by one
      // bought after it. Formatted directly like TimeSection's startDate,
      // never via tzDate, so an admin east of Jakarta still sends the day
      // they picked.
      occurrence_date: scheduleDate?.format(DEFAULT_DATE_SUBMIT_FORMAT),
    });
  const isPrivateClass = selectedClass?.type === ClassCategory.Private;
  const numberOfCustomers = selectedClass?.max_participants ?? 0;

  const showClientSection = !!selectedClass && isPrivateClass; // Only for private classes
  // Time unlocks as soon as class + instructor are set — identical for every
  // entry point (Add button or calendar-slot click). Package credit/expiry
  // bounds are enforced at submit instead (exceedsPackageBounds below).
  const timeEnabled = !!selectedClass && !!instructorId;

  // Submit is enabled only when the form is actually valid: time chosen and,
  // for private/duet, every customer has both a customer and a package picked.
  // Reuses the existing flags/form values — no new gating.
  const allPackagesChosen =
    !isPrivateClass ||
    Array.from({ length: numberOfCustomers }).every(
      (_, i) =>
        !!formValues?.[`customer_id_${i}`] &&
        !!formValues?.[`customer_package_id_${i}`]
    );
  // Fewest sessions any selected package can pay for and earliest expiry
  // across them: bounds the recurrence count and end date so the user can't
  // overrun a package. A session takes the class's cost on the package's tier
  // (credit_cost, constant for a series since a series is one class), so a
  // package pays for floor(available credits ÷ cost) sessions.
  const {
    maxOccurrences,
    maxScheduleDate,
    minScheduleDate,
    maxStartDate,
    minStartFromBookedClass,
  } = useMemo(() => {
    if (!isPrivateClass || !customerPackages)
      return {
        maxOccurrences: undefined,
        maxScheduleDate: undefined,
        minScheduleDate: undefined,
        maxStartDate: undefined,
        minStartFromBookedClass: false,
      };

    // In SESSIONS, not credits (see above).
    let minCredits = Infinity;
    // Ceiling for the LAST occurrence of the series.
    let minExpiry: Date | undefined;
    // Ceiling for the START date: the stored expiry (on an unactivated
    // first-use package that is auto-start + validity, the latest the clock
    // can run to). The series ceiling can be tighter: a start BEFORE the
    // auto-start opens a window of the tier validity counted from the start
    // (its day 1), mirroring the server's checkFirstUseWindow.
    let minStart: Date | undefined;
    // Latest purchase date across the selected packages: the earliest day
    // every one of them can pay for (a backdated class can't be charged to a
    // package bought after it). On a first-use package it can be later: an
    // earlier first class would end the package before a class already
    // booked on it (booking_window.start, System Review C12).
    let maxPurchase: Date | undefined;
    let floorFromBookedClass = false;

    for (let i = 0; i < numberOfCustomers; i++) {
      const cpId = formValues?.[`customer_package_id_${i}`];
      const custOption = formValues?.[`customer_id_${i}`];
      if (!cpId || !custOption) continue;

      const custId = custOption.value ?? custOption;
      const packages = customerPackages[custId];
      if (!packages) continue;

      const pkg = packages.find((cp: CustomerPackage) => cp.id === cpId);
      if (!pkg) continue;

      const remaining = Math.floor(
        getActualRemainingCredits(pkg) / Math.max(1, pkg.credit_cost ?? 1),
      );
      if (remaining < minCredits) minCredits = remaining;
      // The server's window for one new class (booking_window) when it sent
      // one: its end is the stored expiry, or on a first-use package the end
      // date its earliest possible anchor gives (a live waitlist spot
      // included). An older server sends none and gets the stored expiry.
      const bookingWindow = pkg.booking_window;
      const jakartaDay = (key: string) => dayjs.tz(key, DEFAULT_TIMEZONE);
      const expiryDate = bookingWindow
        ? bookingWindow.end
          ? jakartaDay(bookingWindow.end).toDate()
          : undefined
        : pkg.expiry_date
          ? new Date(pkg.expiry_date)
          : undefined;
      if (expiryDate && (!minStart || expiryDate < minStart)) {
        minStart = expiryDate;
      }
      let ceiling = expiryDate;
      const activation = packageActivation(pkg);
      // The anchor a series starting BEFORE it replaces: the server's
      // earliest possible anchor, or (older server) the auto-start of a
      // package whose clock has not started.
      const anchor = bookingWindow
        ? bookingWindow.anchor
        : activation.unactivated
          ? activation.autoStartDate
          : null;
      if (anchor && expiryDate && scheduleDate) {
        const start = tzDate(scheduleDate).startOf("day");
        // A start on/after the anchor keeps the ceiling above (the clock
        // already runs from the anchor by then).
        if (start.isBefore(tzDate(anchor).startOf("day"))) {
          // The length the package was sold with, as the server reads it,
          // counted with the start day as day 1: the last valid day is the
          // day before start + validity (customerPackageExpiryDate).
          const validity = packageValidity(pkg);
          ceiling = start
            .add(
              Math.max(1, validity.expiry_value ?? validity.expiry_duration_by_days),
              (validity.expiry_unit ?? "day") as ManipulateType,
            )
            .subtract(1, "day")
            .toDate();
        }
      }
      if (ceiling && (!minExpiry || ceiling < minExpiry)) {
        minExpiry = ceiling;
      }
      const purchaseDate = pkg.purchase_date
        ? new Date(pkg.purchase_date)
        : undefined;
      const windowStart = bookingWindow ? jakartaDay(bookingWindow.start).toDate() : undefined;
      const floor =
        windowStart && (!purchaseDate || windowStart > purchaseDate)
          ? windowStart
          : purchaseDate;
      if (floor && (!maxPurchase || floor > maxPurchase)) {
        maxPurchase = floor;
        floorFromBookedClass =
          !!windowStart &&
          !!purchaseDate &&
          tzDate(windowStart).startOf("day").isAfter(tzDate(purchaseDate).startOf("day"));
      }
    }

    return {
      maxOccurrences: minCredits === Infinity ? undefined : minCredits,
      maxScheduleDate: minExpiry,
      minScheduleDate: maxPurchase,
      maxStartDate: minStart,
      minStartFromBookedClass: floorFromBookedClass,
    };
  }, [customerPackages, formValues, isPrivateClass, numberOfCustomers, scheduleDate]);

  // With time editable before packages are picked, an already-entered date or
  // recurrence may exceed the selected packages' remaining credits/expiry —
  // re-check those bounds here so such a schedule can never be saved.
  const withinPackageBounds =
    !isPrivateClass ||
    !exceedsPackageBounds({
      recurrenceType: formValues?.recurrence_type ?? "none",
      scheduleDate,
      recurrenceDays: formValues?.recurrence_days,
      endConditionType: formValues?.end_condition_type,
      endConditionCount: formValues?.end_condition_count,
      endConditionUntil: formValues?.end_condition_until,
      maxOccurrences,
      maxScheduleDate,
    });
  // A recurring schedule that ends "on date" must carry a valid end date —
  // present and far enough from the start to yield the minimum sessions. Gate
  // the save on it so an empty/too-close end date disables Jadwalkan rather than
  // only surfacing at submit. Applies to private and open classes alike.
  const recurType = formValues?.recurrence_type ?? "none";
  const isRecurringUntilPath =
    (recurType === "daily" || recurType === "weekly") &&
    (formValues?.end_condition_type ?? "until") === "until";
  const recurringEndDateValid =
    !isRecurringUntilPath ||
    (!!formValues?.end_condition_until &&
      !isUntilDateTooClose({
        recurrenceType: recurType,
        scheduleDate,
        recurrenceDays: formValues?.recurrence_days,
        endConditionUntil: formValues?.end_condition_until,
      }));
  // True only while the per-day time pickers are actually rendered — that
  // Form.Item is preserve={false} and only mounts for weekly recurrence with
  // ≥2 days selected (RecurrenceSelect.tsx), so this can't go stale.
  const perDayTimesActive = !!formValues?.per_day_times;
  // Per-day times: every selected day needs its time picked (the fields
  // initialize to the main start time, so this only blocks a cleared picker).
  const perDayTimesComplete =
    !perDayTimesActive ||
    ((formValues?.recurrence_days ?? []) as string[]).every(
      (day) => !!formValues?.[`day_time_${day}`]
    );
  // A backdated schedule is single-occurrence only. RecurrenceSelect disables
  // daily/weekly for a past start and snaps the type back to "none"; this is
  // the gate's own copy of the rule so Jadwalkan can't race that effect. Past
  // means before NOW — a class earlier today counts — matching the server.
  const combinedStart = combineDateTime(scheduleDate, startTime, DEFAULT_TIMEZONE);
  const isPastStart =
    !!scheduleDate &&
    (tzDate(scheduleDate).isBefore(now(), "day") ||
      (!!combinedStart && combinedStart.isBefore(now())));
  const canSubmit =
    timeEnabled &&
    // The top-level start_time field is hidden (TimeSection.tsx) once
    // per-day times are active, so it's no longer meaningful to require —
    // perDayTimesComplete already covers completeness for that mode.
    (perDayTimesActive || !!startTime) &&
    !timingBlockedByAvailability &&
    !(isPastStart && recurType !== "none") &&
    allPackagesChosen &&
    withinPackageBounds &&
    recurringEndDateValid &&
    perDayTimesComplete;

  // ── Click-to-create wiring ──
  // Seed the date/time from the clicked slot (on open and on every re-click).
  // The seed that OPENS the panel leaves it clean (an exploratory slot click
  // then X still closes silently), but a slot click while the panel is
  // already up is the admin CHANGING the date/time — mark it dirty like a
  // manual picker edit would be, or the close prompt and Simpan Draft never
  // learn about it (setFieldsValue doesn't fire onValuesChange).
  const seedEffectRanRef = useRef(false);
  useEffect(() => {
    if (createDraft) {
      form.setFieldsValue({
        schedule_date: createDraft.date,
        start_time: createDraft.startTime,
      });
      if (seedEffectRanRef.current) {
        setIsDirty(true);
      }
    }
    seedEffectRanRef.current = true;
  }, [createDraft, form]);

  // Restore the panel's state after a round trip to a client's detail page
  // (see ClientPackageSection.tsx's "Kelola Paket Klien" button). Runs once
  // on mount — the drawer only remounts when reopened, and resumeDraft is
  // meant to be applied exactly once per reopen (the parent clears it via
  // onResumeDraftConsumed right after).
  useEffect(() => {
    if (resumeDraft) {
      const { formValues, selectedClass: draftClass, customerIds: draftCustomerIds } =
        applyCreateScheduleResumeDraft(resumeDraft);
      setSelectedClass(draftClass);
      setCustomerIds(draftCustomerIds);
      form.setFieldsValue(formValues);
      // A draft resumed from the saved list carries its row id — remember it
      // so re-saves update that row and Jadwalkan cleans it up.
      setActiveDraftId(resumeDraft.savedDraftId ?? null);
      // Content straight from a stored row starts clean; a Kelola-Paket round
      // trip carries live unsaved edits, so it resumes dirty (see the
      // pristine comment in scheduleResumeDraft.ts).
      setIsDirty(!resumeDraft.pristine);
      onResumeDraftConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Staleness reconciles for resumed SAVED drafts ──
  // A stored draft snapshots the whole ClassType; by resume time the class may
  // have been edited (duration/max_participants drive the form's shape) or
  // deleted. Re-resolve against the live class list: found → adopt the live
  // object (self-stabilizing — the next run sees reference equality); gone →
  // warn and clear the class the way handleClassChange's reset does, keeping
  // the date/time the admin already set.
  useEffect(() => {
    if (!classes || !selectedClass) return;
    const live = classes.find((c) => c.id === selectedClass.id);
    if (live === selectedClass) return;
    if (live) {
      setSelectedClass(live);
      return;
    }
    message.warning(translate("schedule.drafts.class_missing"));
    const schedule_date = form.getFieldValue("schedule_date");
    const start_time = form.getFieldValue("start_time");
    setSelectedClass(undefined);
    setCustomerIds("");
    form.resetFields();
    form.setFieldsValue({ schedule_date, start_time });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, selectedClass]);

  // A draft can also hold a customer_package_id that no longer exists for that
  // client (consumed, expired, invalidated). The bounds loop above skips
  // unknown ids (`if (!pkg) continue`) and allPackagesChosen only checks
  // presence, so a dead id would sail through gating and submit — clear it
  // instead, which flips allPackagesChosen false and disables Jadwalkan until
  // the admin re-picks. Also covers the pre-existing Kelola-Paket round trip.
  useEffect(() => {
    if (!isPrivateClass || !customerPackages) return;
    for (let i = 0; i < numberOfCustomers; i++) {
      const cpId = form.getFieldValue(`customer_package_id_${i}`);
      const custOption = form.getFieldValue(`customer_id_${i}`);
      if (!cpId || !custOption) continue;
      const custId = custOption.value ?? custOption;
      const packages = customerPackages[custId];
      if (!packages) continue;
      if (!packages.some((cp: CustomerPackage) => cp.id === cpId)) {
        form.setFieldValue(`customer_package_id_${i}`, undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPackages, numberOfCustomers, isPrivateClass]);

  // A resumed draft (or a Kelola-Paket round trip) can hold a client who has
  // been deactivated since: the picker only lists active clients, so the
  // seat would render as a bare id and the server refuses the create. Clear
  // that seat and its package, with a warning, like a deleted class above.
  useEffect(() => {
    if (!isPrivateClass || !customers) return;
    const active = new Set(customers.map((c) => c.id));
    let cleared = false;
    for (let i = 0; i < numberOfCustomers; i++) {
      const custOption = form.getFieldValue(`customer_id_${i}`);
      if (!custOption) continue;
      const custId = custOption.value ?? custOption;
      if (active.has(custId)) continue;
      form.setFieldValue(`customer_id_${i}`, undefined);
      form.setFieldValue(`customer_package_id_${i}`, undefined);
      cleared = true;
    }
    if (cleared) {
      message.warning(translate("schedule.drafts.client_deactivated"));
      handleCustomerChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, numberOfCustomers, isPrivateClass]);

  // Publish the live ghost preview(s) from the current form values so the
  // calendar can draw them. No start time yet (e.g. opened via the Add
  // button) → no ghost. A weekly recurrence with 1+ days selected gets one
  // ghost per selected day whose occurrence falls in the CURRENTLY VISIBLE
  // Jadwal week (state.view.date) — so navigating the calendar to a later
  // week while creating shows that week's boxes too, not just the first —
  // at its per-day time if "Waktu berbeda per hari" is on, or the shared
  // start_time otherwise. Previously only the day matching "Tanggal Mulai"
  // ever got a box, regardless of which week was showing. The Tanggal-Mulai
  // weekday (if it has an occurrence in the visible week) is always ordered
  // first so the calendar's auto-scroll (CalendarView.tsx, keyed off the
  // first entry) keeps following whatever the admin is editing via the
  // top-level date/time fields, same as before this change.
  useEffect(() => {
    if (!scheduleDate) {
      setGhostPreviews([]);
      return;
    }
    const recurrenceDays =
      formValues?.recurrence_type === "weekly"
        ? ((formValues?.recurrence_days ?? []) as string[])
        : [];
    if (recurrenceDays.length === 0) {
      if (!startTime) {
        setGhostPreviews([]);
        return;
      }
      setGhostPreviews([
        {
          date: scheduleDate,
          startTime,
          durationMinutes: selectedClass?.duration ?? 60,
          className: selectedClass?.name,
          instructorId: instructorId?.value ?? instructorId,
        },
      ]);
      return;
    }

    const weekStart = tzDate(state.view.date).startOf("week");
    const weekEnd = weekStart.endOf("week");
    const endCondition: RecurrenceEndCondition =
      formValues?.end_condition_type === "count"
        ? { type: "count", count: formValues?.end_condition_count }
        : { type: "until", until: formValues?.end_condition_until };
    const daysThisWeek = occurrencesInWeek(
      scheduleDate,
      recurrenceDays,
      endCondition,
      weekStart,
      weekEnd
    );

    const anchorCode = DAYJS_TO_RRULE_DAY[tzDate(scheduleDate).day()];
    const orderedDays = daysThisWeek.includes(anchorCode)
      ? [anchorCode, ...daysThisWeek.filter((day) => day !== anchorCode)]
      : daysThisWeek;
    const previews = orderedDays.reduce<ScheduleGhostPreview[]>(
      (acc, day) => {
        const dayTime = perDayTimesActive
          ? (formValues?.[`day_time_${day}`] as Dayjs | undefined)
          : startTime;
        if (dayTime) {
          acc.push({
            date: dateInWeek(weekStart, day),
            startTime: dayTime,
            durationMinutes: selectedClass?.duration ?? 60,
            className: selectedClass?.name,
            instructorId: instructorId?.value ?? instructorId,
          });
        }
        return acc;
      },
      []
    );
    setGhostPreviews(previews);
  }, [
    scheduleDate,
    startTime,
    perDayTimesActive,
    formValues,
    selectedClass,
    instructorId,
    state.view.date,
    setGhostPreviews,
  ]);

  // Clear the draft + ghosts whenever the panel unmounts (closed by any means).
  useEffect(() => {
    return () => {
      setGhostPreviews([]);
      setCreateDraft(null);
      setTimingBlockedByAvailability(false);
    };
  }, [setGhostPreviews, setCreateDraft]);

  // ── Handlers ──
  const handleClassChange = (value: string) => {
    const cls = classes?.find((c) => c.id === value);
    // Preserve the prefilled (or manually set) date/time across the reset that
    // clears class-dependent client selections.
    const schedule_date = form.getFieldValue("schedule_date");
    const start_time = form.getFieldValue("start_time");
    setSelectedClass(cls);
    setCustomerIds("");
    // Picking a class is a real edit even though the resetFields below wipes
    // AntD's touched state (see isDirty's comment).
    setIsDirty(true);
    form.resetFields();
    form.setFieldsValue({ class_id: value, schedule_date, start_time });
  };

  const handleCustomerChange = () => {
    const ids = Array.from({ length: numberOfCustomers }, (_, i) => {
      const opt = form.getFieldValue(`customer_id_${i}`);
      return opt ? opt.value ?? opt : undefined;
    })
      .filter(Boolean)
      .join(",");
    setCustomerIds(ids);
  };

  // Actually closes the panel (the parent unmounts it, so this is the point of
  // no return for unsaved edits). Reached only via requestClose's dirty check,
  // a successful save/create, or the prompt's Buang.
  const closePanel = () => {
    form.resetFields();
    setSelectedClass(undefined);
    setCustomerIds("");
    setCreateDrawerOpen(false);
  };

  // The header X and Batal both land here: a clean form closes silently, a
  // dirty one gets the three-way prompt (Simpan Draft / Buang / stay).
  const requestClose = () => {
    if (isDirty) {
      setClosePromptOpen(true);
    } else {
      closePanel();
    }
  };

  // Snapshots the panel exactly like handleBuyPackage and persists it via
  // /v2/schedule-draft — PUT when this panel was resumed from a saved draft,
  // POST otherwise. The label is derived (class name), not asked for: the
  // list derives everything else it shows from the payload.
  const handleSaveDraft = () => {
    const payload = buildScheduleDraftPayload(
      buildCreateScheduleResumeDraft(
        PATHS.SCHEDULE,
        form.getFieldsValue(true),
        selectedClass,
        customerIds
      )
    );
    const label = selectedClass?.name ?? null;
    const onSuccess = () => {
      setClosePromptOpen(false);
      closePanel();
    };
    if (activeDraftId) {
      updateDraftMutate({ draft_id: activeDraftId, label, payload }, { onSuccess });
    } else {
      createDraftMutate({ label, payload }, { onSuccess });
    }
  };

  // A schedule created from a resumed draft retires the draft (email-draft
  // semantics: sending deletes the draft). Fire-and-forget: a failed cleanup
  // only leaves a row the admin can delete from the list.
  const handleScheduleCreated = () => {
    if (activeDraftId) {
      deleteDraftSilently(activeDraftId);
    }
    closePanel();
  };

  // Navigates to the client's detail page to view/add packages &
  // memberships (see ClientPackageSection.tsx) — snapshots the panel's
  // current state first so it can be restored on return instead of the
  // admin having to start the schedule over. Router `state` carries the
  // draft through the round trip; nothing is persisted, so there's nothing
  // to clean up if the admin navigates away some other way instead.
  const handleBuyPackage = (customerId: string) => {
    const draft = buildCreateScheduleResumeDraft(
      PATHS.SCHEDULE,
      form.getFieldsValue(true),
      selectedClass,
      customerIds,
      // Keep the saved-draft link across the round trip so re-saving on
      // return still updates the same row.
      activeDraftId ?? undefined
    );
    navigate(`${PATHS.CUSTOMER}/${customerId}`, {
      state: { scheduleDraft: draft },
    });
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const customerPackageIds = isPrivateClass
          ? Array.from(
              { length: numberOfCustomers },
              (_, i) => values[`customer_package_id_${i}`]
            ).filter(Boolean)
          : undefined;
        // One entry per SEAT, carrying who sits in it. customer_package_ids
        // alone forces the server to read the attendee off the package owner,
        // which is wrong the moment two seats share one package (and a repeated
        // id would collapse to a single booking). Sent alongside the legacy
        // array, which the server still accepts from older clients.
        const participants = isPrivateClass
          ? Array.from({ length: numberOfCustomers }, (_, i) => {
              // The client Select is labelInValue, so the form holds
              // { value, label } and NOT a bare id. Every other reader of this
              // field unwraps it the same way (handleCustomerChange, the
              // stale-package prune, the picker's eligibility pass); sending
              // the option object straight through made the server reject the
              // whole request with "expected string, received object" on
              // participants[i].customer_id, which blocked every private,
              // duet and triplet booking.
              const custOption = values[`customer_id_${i}`];
              return {
                customer_id: custOption?.value ?? custOption,
                customer_package_id: values[`customer_package_id_${i}`],
              };
            }).filter((p) => p.customer_id && p.customer_package_id)
          : undefined;

        const recurrenceType = (values.recurrence_type ??
          "none") as RecurrenceType;
        const resolvedInstructorId =
          values.instructor_id.value ?? values.instructor_id;

        // Default to "until" with a 1-year horizon — matches backend migration
        // policy and ensures buildRRule always emits a finite RRULE.
        const untilEnd: RecurrenceEnd = {
          type: "until",
          date:
            (values.end_condition_until as Dayjs | undefined)?.toISOString() ??
            now().add(1, "year").toISOString(),
        };
        const endType =
          recurrenceType === "weekly" || recurrenceType === "daily"
            ? values.end_condition_type ?? "until"
            : "until";

        const clashForm = (startIso: string) => ({
          class_id: values.class_id,
          instructor_id: resolvedInstructorId,
          customer_ids: isPrivateClass ? selectedCustomerIds : undefined,
          start_time: startIso,
        });

        // Per-day times: one series per distinct start time, created
        // atomically via the batch endpoint. A single distinct time falls
        // through to the plain create (same behavior, but the series' time is
        // the per-day pickers' shared value, which can differ from the main
        // start_time field).
        const perDayDays = (values.recurrence_days ?? []) as string[];
        if (
          recurrenceType === "weekly" &&
          values.per_day_times &&
          perDayDays.length >= 2
        ) {
          const timeByDay = new Map<string, Dayjs>();
          for (const day of perDayDays) {
            timeByDay.set(
              day,
              (values[`day_time_${day}`] as Dayjs | undefined) ??
                (values.start_time as Dayjs)
            );
          }
          const series = buildPerDaySeries(
            values.schedule_date as Dayjs,
            perDayDays,
            timeByDay,
            endType === "count"
              ? (values.end_condition_count as number)
              : undefined
          );

          const schedules = series.map((s) => ({
            class_id: values.class_id as string,
            instructor_id: resolvedInstructorId as string,
            customer_package_ids: customerPackageIds,
            participants,
            start_time: combineDateTime(
              s.startDate,
              s.time,
              DEFAULT_TIMEZONE
            )!.toISOString(),
            rrule: buildRRule(
              "weekly",
              s.count !== undefined
                ? { type: "count", count: s.count }
                : untilEnd,
              s.days,
              DEFAULT_TIMEZONE
            ),
            timezone: DEFAULT_TIMEZONE,
          }));

          if (schedules.length === 1) {
            checkAndConfirm(clashForm(schedules[0].start_time), () =>
              createScheduleV2(schedules[0], {
                onSuccess: () => handleScheduleCreated(),
              })
            );
          } else {
            checkManyAndConfirm(
              schedules.map((s) => clashForm(s.start_time)),
              () =>
                createScheduleBatchV2(
                  { schedules },
                  { onSuccess: () => handleScheduleCreated() }
                )
            );
          }
          return;
        }

        const start = combineDateTime(
          values.schedule_date as Dayjs,
          values.start_time as Dayjs,
          DEFAULT_TIMEZONE
        );
        const startIso = start ? start.toISOString() : "";

        const byDay =
          recurrenceType === "weekly" ? values.recurrence_days : undefined;
        const endCondition: RecurrenceEnd =
          endType === "count"
            ? { type: "count", count: values.end_condition_count }
            : untilEnd;

        const rrule = buildRRule(
          recurrenceType,
          endCondition,
          byDay,
          DEFAULT_TIMEZONE
        );

        // Clashing creates are allowed — gate behind the clash warning. For
        // private classes the involved clients are checked too; open classes
        // have no pre-assigned clients yet.
        const submit = () =>
          checkAndConfirm(clashForm(startIso), () =>
            createScheduleV2(
              {
                class_id: values.class_id,
                instructor_id: resolvedInstructorId,
                customer_package_ids: customerPackageIds,
                participants,
                start_time: startIso,
                rrule,
                timezone: DEFAULT_TIMEZONE,
              },
              { onSuccess: () => handleScheduleCreated() }
            )
          );

        // A start in the past means the admin is recording a class that
        // already happened — confirm that explicitly, spelling out what
        // happens to the participants' bookings. A private roster is created
        // Confirmed with credits on hold; an open class starts empty and
        // participants are added on Detail Jadwal. Either way the bookings
        // sit on an occurrence that has already ended, so the
        // auto-complete sweep marks them Completed within 5 minutes IF the
        // studio has it on for this class type (AutoCompletePolicyPage:
        // master switch + scope, anything non-open counting as private,
        // mirroring the server's sweep filter). Otherwise the copy says so
        // and tells the admin the bookings still need marking by hand.
        // Settings not loaded yet reads as "manual" — the harmless direction.
        if (start && start.isBefore(now())) {
          const scope = businessSettings?.auto_complete_scope;
          const autoCompletesThisType =
            !!businessSettings?.auto_complete_bookings &&
            (scope === AutoCompleteScope.All ||
              scope ===
                (isPrivateClass
                  ? AutoCompleteScope.Private
                  : AutoCompleteScope.Open));
          const bodyKey = `schedule.drawer.backdate_confirm_${
            isPrivateClass ? "private" : "open"
          }_${autoCompletesThisType ? "auto" : "manual"}`;
          showConfirmModal({
            tone: "amber",
            title: translate("schedule.drawer.backdate_confirm_title"),
            content: translate(bodyKey),
            confirmLabel: translate("schedule.drawer.backdate_confirm_button"),
            onConfirm: submit,
          });
          return;
        }
        submit();
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
      });
  };

  return (
    <Flex
      vertical
      style={{
        // Full-height left rail anchored to the viewport edge — overlays the
        // icon nav sider while creating (Google Calendar style); the nav
        // returns when the panel closes. Header/footer are fixed, the
        // sections scroll in between.
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        width: CREATE_PANEL_WIDTH,
        zIndex: 100,
        background: colors.card,
        borderRight: `1px solid ${colors.line}`,
        overflow: "hidden",
      }}
    >
      {/* Header — single title + close affordance */}
      <Flex
        justify="space-between"
        align="center"
        style={{ padding: "16px 18px 8px" }}
      >
        <Typography.Title
          level={5}
          style={{ margin: 0, fontFamily: "var(--font-display)" }}
        >
          {translate("schedule.drawer.title")}
        </Typography.Title>
        <Button type="text" icon={<CloseOutlined />} onClick={requestClose} />
      </Flex>

      {/* Body — progressive sections (Kelas, Waktu, Klien & Paket) */}
      <Flex
        vertical
        style={{ flex: 1, overflowY: "auto", padding: "8px 18px 18px" }}
      >
        <Form
          form={form}
          layout="vertical"
          name="createScheduleV2Form"
          // User-driven control changes only (never setFieldsValue), so the
          // click-to-create seed and resumed drafts don't mark the form dirty.
          onValuesChange={() => setIsDirty(true)}
        >
          <ClassSection
            classes={classes}
            classLoading={classLoading}
            staffs={instructors}
            staffLoading={instructorsLoading}
            selectedClass={selectedClass}
            selectedCustomerIds={selectedCustomerIds}
            onClassChange={handleClassChange}
          />
          <TimeSection
            form={form}
            selectedClass={selectedClass}
            scheduleDate={scheduleDate}
            startTime={startTime}
            timeEnabled={timeEnabled}
            maxOccurrences={maxOccurrences}
            maxScheduleDate={maxScheduleDate}
            maxStartDate={maxStartDate}
            minStartFromBookedClass={minStartFromBookedClass}
            minScheduleDate={minScheduleDate}
            allowPastDates
            sectionNumber={2}
            instructorId={resolvedInstructorId}
            onAvailabilityBlockChange={setTimingBlockedByAvailability}
          />
          {showClientSection && (
            <ClientPackageSection
              form={form}
              customers={customers}
              customerLoading={customerLoading}
              customerPackages={customerPackages}
              customerPackageLoading={customerPackageLoading}
              selectedCustomerIds={selectedCustomerIds}
              numberOfCustomers={numberOfCustomers}
              isPrivateClass={isPrivateClass}
              onCustomerChange={handleCustomerChange}
              onBuyPackage={handleBuyPackage}
            />
          )}
        </Form>
      </Flex>

      {/* Footer — Batal + blue Jadwalkan pinned to the bottom of the rail,
          always visible; the primary action stays disabled until valid */}
      <Flex
        gap={10}
        align="center"
        style={{
          padding: "14px 18px",
          borderTop: `1px solid ${colors.line}`,
        }}
      >
        {/* modalButtonsTheme: dismiss renders solid ink (Batal), primary renders
            navy (Jadwalkan) — the design's dismiss=ink / confirm=navy system.
            ConfigProvider adds no DOM, so the flex split is preserved. Simpan
            Draft sits between them in the app's normal outlined look
            (outlinedButtonTheme escape hatch) so the primary stays dominant. */}
        <ConfigProvider theme={modalButtonsTheme}>
          <Button onClick={requestClose} style={{ flex: 1 }}>
            {translate("button.cancel")}
          </Button>
          <ConfigProvider theme={outlinedButtonTheme}>
            <Button
              onClick={handleSaveDraft}
              loading={isSavingDraft}
              disabled={!isDirty && !selectedClass}
              style={{ flex: 1 }}
            >
              {translate("schedule.drafts.save")}
            </Button>
          </ConfigProvider>
          <Button
            type="primary"
            onClick={onSubmit}
            loading={isPending || isBatchPending}
            disabled={!canSubmit}
            style={{ flex: 2 }}
          >
            {translate("button.schedule")}
          </Button>
        </ConfigProvider>
      </Flex>
      {clashWarning}
      <SaveDraftClosePrompt
        open={closePromptOpen}
        resumed={!!activeDraftId}
        saving={isSavingDraft}
        onSaveDraft={handleSaveDraft}
        onDiscard={() => {
          setClosePromptOpen(false);
          closePanel();
        }}
        onStay={() => setClosePromptOpen(false)}
      />
    </Flex>
  );
};

export default CreateScheduleDrawer;
